import { fontKey, toCanvasFont } from "./text";

const createMeasureContext = () => {
  if (typeof OffscreenCanvas !== "undefined") {
    return new OffscreenCanvas(1, 1).getContext("2d");
  }
  if (typeof document !== "undefined") {
    return document.createElement("canvas").getContext("2d");
  }
  return null;
};

export const applyCanvasFont = (ctx, font) => {
  ctx.font = toCanvasFont(font);
  if ("fontKerning" in ctx) ctx.fontKerning = font.kerning ? "normal" : "none";
  if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";
};

const countChars = (text) => Array.from(text).length;

/**
 * Measurer backed by a 2D canvas context. Results are cached per font and
 * string; create a new measurer after web fonts finish loading.
 */
export function createCanvasMeasurer(ctx = createMeasureContext()) {
  if (!ctx) throw new Error("Canvas 2D context is unavailable");
  const widths = new Map();
  const metrics = new Map();
  let activeKey = null;

  const use = (font) => {
    const key = fontKey(font);
    if (key !== activeKey) {
      applyCanvasFont(ctx, font);
      activeKey = key;
    }
    return key;
  };

  return {
    width(font, text) {
      if (!text) return 0;
      const key = `${fontKey(font)}\u0000${text}`;
      const cached = widths.get(key);
      if (cached !== undefined) return cached;
      use(font);
      // CSS letter-spacing is added after every character, including the last.
      const value =
        ctx.measureText(text).width + font.letterSpacing * countChars(text);
      widths.set(key, value);
      return value;
    },
    metrics(font) {
      const key = fontKey(font);
      const cached = metrics.get(key);
      if (cached) return cached;
      use(font);
      const sample = ctx.measureText("Hg");
      const cap = ctx.measureText("H");
      const value = {
        ascent: sample.fontBoundingBoxAscent ?? font.size * 0.8,
        descent: sample.fontBoundingBoxDescent ?? font.size * 0.2,
        cap: cap.actualBoundingBoxAscent ?? font.size * 0.7,
      };
      metrics.set(key, value);
      return value;
    },
  };
}

/**
 * Deterministic measurer for unit tests: every character is
 * `charWidth × size` wide, ascent/descent/cap are fixed fractions of size.
 */
export function createFixedMeasurer({
  charWidth = 0.5,
  ascent = 0.8,
  descent = 0.2,
  cap = 0.7,
} = {}) {
  return {
    width(font, text) {
      if (!text) return 0;
      const n = countChars(text);
      return n * charWidth * font.size + font.letterSpacing * n;
    },
    metrics(font) {
      return {
        ascent: ascent * font.size,
        descent: descent * font.size,
        cap: cap * font.size,
      };
    },
  };
}
